import React, { useCallback, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Box, Inscription, RoundDoneIcon } from '@common-fe/common-fe';
import _ from 'lodash';

import ROUTES from '@/common/routes';
import { useHistory } from '@/modules/core/hooks';
import { STEPS } from '@/modules/user/components/SignIn/signIn.types';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';
import { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

import { EMAIL_FIELD_KEY } from '../ForgotUsername/ForgotUsername';

import useInitialStepFromUrl from './useInitialStepFromUrl';

const FORGOT_USERNAME_FIELD_KEY = 'forgotUsername';
export const FORGOT_SUBTITLE =
  'Please enter the username associated with your account and a code will be emailed to you.';

const useSignInStepper = () => {
  const history = useHistory();
  const [step, setStep] = useState<STEPS>(STEPS.SIGN_IN);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const isChangePasswordMode = useRouteMatch().path === ROUTES.CHANGE_PASSWORD;

  useInitialStepFromUrl(setStep);

  const defaultTitleText = useMemo(() => {
    if (whiteLabelingConfig?.key === THEMES.gusto) {
      return (
        <Inscription textAlign="center" weight={700} size="3xl" style={{ letterSpacing: '-0.5px' }}>
          Sign in to Gusto to continue
        </Inscription>
      );
    }
    return 'Welcome!';
  }, []);

  const defaultSubtitleText = useMemo(() => {
    if (whiteLabelingConfig?.key === THEMES.gusto) {
      return (
        <Inscription margin={{ top: 'spacing16' }} style={{ display: 'block' }}>
          To access your admin panel powered by Elevate,
          <br />
          sign to your Gusto account.
        </Inscription>
      );
    }
    return '';
  }, []);

  const title = useMemo(() => {
    if (step === STEPS.FORGOT_PASSWORD_SUCCESS) {
      return (
        <Box align="center">
          <RoundDoneIcon />
          <Inscription margin={{ top: 'spacing8' }} weight="bold" size="2xl" lineHeight="32px">
            Password Updated
          </Inscription>
        </Box>
      );
    }
    if (isChangePasswordMode) {
      return 'Change Password';
    }
    if (step === STEPS.FORGOT_PASSWORD) {
      return 'Forgot Password?';
    }
    if (step === STEPS.FORGOT_USERNAME) {
      return 'Forgot Username?';
    }
    if (step === STEPS.FORGOT_USERNAME_SUCCESS) {
      return (
        <Box align="center">
          <RoundDoneIcon />
          <Box margin={{ top: 'spacing24', bottom: 'spacing4' }}>
            <Inscription weight="bold" size="2xl" lineHeight="32px">
              {email}
            </Inscription>
          </Box>
        </Box>
      );
    }
    if (step === STEPS.NEW_PASSWORD) {
      return '';
    }
    return defaultTitleText;
  }, [isChangePasswordMode, step, email, defaultTitleText]);

  const handlePreviousStep = useCallback(() => {
    if (step === STEPS.FORGOT_PASSWORD) {
      setStep(STEPS.SIGN_IN);
    } else if (step === STEPS.FORGOT_USERNAME) {
      setStep(STEPS.SIGN_IN);
    } else if (step === STEPS.NEW_PASSWORD) {
      setStep(STEPS.FORGOT_PASSWORD);
    }
  }, [step]);

  const isBackButton = useMemo(
    () => step === STEPS.FORGOT_PASSWORD || step === STEPS.FORGOT_USERNAME || step === STEPS.NEW_PASSWORD,
    [step]
  );
  const subtitle = useMemo(() => {
    if (step === STEPS.FORGOT_PASSWORD) {
      return 'Please enter the username associated with your account and a code will be emailed to you.';
    }
    if (step === STEPS.FORGOT_USERNAME) {
      return 'Please enter the email address associated with your account and a code will be emailed to you.';
    }
    if (step === STEPS.FORGOT_USERNAME_SUCCESS) {
      return 'If the email is on file, you will receive an email with your username';
    }
    if (step === STEPS.NEW_PASSWORD) {
      return '';
    }
    if (step === STEPS.FORGOT_PASSWORD_SUCCESS) {
      return 'You can now sign in with your new password.';
    }
    return defaultSubtitleText;
  }, [step, defaultSubtitleText]);

  const handleNextStep = useCallback(
    async (value?: object) => {
      switch (step) {
        case STEPS.FORGOT_PASSWORD: {
          const currentUsername: string = _.get(value, FORGOT_USERNAME_FIELD_KEY, '');
          setUsername(currentUsername);
          break;
        }
        case STEPS.NEW_PASSWORD:
          setStep(STEPS.FORGOT_PASSWORD_SUCCESS);
          break;
        case STEPS.FORGOT_USERNAME_SUCCESS:
          setStep(STEPS.SIGN_IN);
          break;
        case STEPS.FORGOT_USERNAME: {
          const userEmail: string = _.get(value, EMAIL_FIELD_KEY, '');
          setStep(STEPS.FORGOT_USERNAME_SUCCESS);
          setEmail(userEmail);
          break;
        }
        case STEPS.FORGOT_PASSWORD_SUCCESS: {
          history.push(ROUTES.LOGIN);
          setStep(STEPS.SIGN_IN);
          break;
        }
        default:
          setStep(STEPS.FORGOT_PASSWORD);
      }
    },
    [step, history]
  );

  return {
    step,
    username,
    title,
    subtitle,
    isBackButton,
    isChangePasswordMode,
    handlePreviousStep,
    handleNextStep,
    handleForgotUsername: () => setStep(STEPS.FORGOT_USERNAME),
  };
};

export default useSignInStepper;
